import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  ToggleRadioButtonGroup,
  type ToggleRadioButtonGroupProps,
} from '~/components/elements/ToggleRadioButtonGroup'
import {
  compact,
  type SegmentDisplayType,
  segmentDisplayTypeLabelI18nKeyMap,
  segmentDisplayTypes,
} from '~/utils'

type Attribute =
  ToggleRadioButtonGroupProps<SegmentDisplayType>['attributes'][number]

export type SegmentDisplaySwitcherInnerProps = {
  'data-testid'?: string
  disabled?: boolean
  onChange?: (value: SegmentDisplayType) => void
  value: SegmentDisplayType
  withBoth?: boolean
  withSummary?: boolean
}

export const SegmentDisplaySwitcherInner = ({
  'data-testid': dataTestId,
  disabled,
  onChange,
  value,
  withBoth = false,
  withSummary = false,
}: SegmentDisplaySwitcherInnerProps) => {
  const { t } = useTranslation()

  const attributeMap = React.useMemo(
    () =>
      R.mapToObj(segmentDisplayTypes, (segmentDisplayType) => [
        segmentDisplayType,
        {
          text: t(segmentDisplayTypeLabelI18nKeyMap[segmentDisplayType]),
          value: segmentDisplayType,
        } satisfies Attribute,
      ]),
    [t],
  )

  const attributes = React.useMemo<Attribute[]>(
    () =>
      compact([
        attributeMap.fiveSegs,
        attributeMap.nineSegs,
        withBoth && attributeMap.both,
        withSummary && attributeMap.summary,
      ]),
    [attributeMap, withBoth, withSummary],
  )

  return (
    <ToggleRadioButtonGroup
      attributes={attributes}
      data-testid={dataTestId}
      disabled={disabled}
      value={value}
      onChange={onChange}
    />
  )
}
